import { useEffect, useRef, useState } from "react";
import { createMatch, restartRound, stepMatch } from "@marshmallow-duel/simulation";

import { GameAudio } from "../game/audio";
import { InputManager } from "../game/input";
import { renderMatch } from "../game/renderer";
import type { GamePreferences } from "../game/preferences";
import { ScaledStage } from "./ScaledStage";

import type { ArenaMap, Language, MatchRules, MatchState, PlayerProfile, PlayerSlot } from "@marshmallow-duel/simulation";
import type { TranslationKey } from "../i18n";

interface GameScreenProps {
  readonly language: Language;
  readonly map: ArenaMap;
  readonly rules: MatchRules;
  readonly profiles: Readonly<Record<PlayerSlot, PlayerProfile>>;
  readonly preferences: GamePreferences;
  readonly t: (key: TranslationKey) => string;
  readonly onLeave: () => void;
  readonly onSelectMap?: () => void;
  readonly onMatchWinner?: (winner: PlayerSlot) => void;
}

const seed = () => (Date.now() ^ Math.floor(performance.now() * 1000)) >>> 0;

export const GameScreen = ({ map, rules, profiles, preferences, t, onLeave, onSelectMap, onMatchWinner }: GameScreenProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const onMatchWinnerRef = useRef(onMatchWinner);
  const stateRef = useRef<MatchState>(createMatch({ mode: "local", map, rules, profiles, seed: seed() }));
  const [phase, setPhase] = useState(stateRef.current.phase);
  const [roundWinner, setRoundWinner] = useState<PlayerSlot | null>(null);
  const [matchWinner, setMatchWinner] = useState<PlayerSlot | null>(null);

  useEffect(() => { onMatchWinnerRef.current = onMatchWinner; }, [onMatchWinner]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const input = new InputManager(preferences.gamepads);
    const audio = new GameAudio(preferences.sound, preferences.screaming);
    if (preferences.notifier) audio.play("snd_versus");
    if (preferences.music) audio.startMusic((Math.floor(Math.random() * 6) + 1));
    input.attach();
    let frame = 0;
    let previous = performance.now();
    let accumulator = 0;
    let previousPhase = stateRef.current.phase;
    let previousRoundWinner = stateRef.current.roundWinner;
    let previousMatchWinner = stateRef.current.matchWinner;
    let renderedFrames = 0;
    let fpsWindow = previous;
    let fps = 0;
    const loop = (now: number) => {
      accumulator = Math.min(accumulator + now - previous, 500);
      previous = now;
      while (accumulator >= 1000 / stateRef.current.roomSpeed) {
        const tickDuration = 1000 / stateRef.current.roomSpeed;
        const state = stepMatch(stateRef.current, input.read(rules.playerCount));
        audio.playEvents(state.events);
        if (state.phase !== previousPhase) {
          previousPhase = state.phase;
          setPhase(state.phase);
          if (state.phase === "matchOver" && state.matchWinner) {
            onMatchWinnerRef.current?.(state.matchWinner);
            if (preferences.saveWinner) canvas.toBlob((blob) => {
              if (!blob) return;
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = `mdr-${new Date().toISOString().replace(/[:.]/g, "-")}.png`;
              link.click();
              setTimeout(() => URL.revokeObjectURL(link.href), 0);
            });
          }
        }
        if (state.roundWinner !== previousRoundWinner) { previousRoundWinner = state.roundWinner; setRoundWinner(state.roundWinner); }
        if (state.matchWinner !== previousMatchWinner) { previousMatchWinner = state.matchWinner; setMatchWinner(state.matchWinner); }
        accumulator -= tickDuration;
      }
      renderedFrames += 1;
      if (now - fpsWindow >= 500) {
        fps = renderedFrames * 1000 / (now - fpsWindow);
        renderedFrames = 0;
        fpsWindow = now;
      }
      renderMatch(canvas, stateRef.current, { oldTextures: preferences.oldTextures, raining: preferences.raining, showFps: preferences.showFps, fps });
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(frame);
      input.detach();
      audio.stopMusic();
    };
  }, [preferences, rules.playerCount]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code === "Escape") onLeave();
      if (event.code === "Enter" && stateRef.current.phase === "roundOver") {
        stateRef.current = restartRound(stateRef.current);
        setPhase(stateRef.current.phase);
        setRoundWinner(null);
      }
      if (stateRef.current.phase === "matchOver") {
        if (event.code === "KeyR") restartMatch();
        else if (event.code === "KeyM") onSelectMap?.();
        else if (event.code === "Enter") onLeave();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [map, onLeave, onSelectMap, profiles, rules]);

  const continueRound = () => {
    stateRef.current = restartRound(stateRef.current);
    setPhase(stateRef.current.phase);
    setRoundWinner(null);
  };

  const restartMatch = () => {
    stateRef.current = createMatch({ mode: "local", map, rules, profiles, seed: seed() });
    setPhase(stateRef.current.phase);
    setRoundWinner(null);
    setMatchWinner(null);
  };

  const winner = matchWinner ?? roundWinner;
  return (
    <ScaledStage className="game-stage">
      <canvas ref={canvasRef} width={800} height={600} aria-label={`Marshmallow Duel Reloaded – ${map.name}`} />
      {phase === "countdown" && (
        <div className="game-intro">
          <img src="/assets/original/backgrounds/bg_menu_title.png" alt="Marshmallow Duel Reloaded" />
          <p>{t("game.map")} {map.name}</p>
          <strong>{stateRef.current.players.map(({ profile }) => profile.name).join(" vs ")}</strong>
        </div>
      )}
      {(phase === "roundOver" || phase === "matchOver") && (
        <div className="game-results">
          <strong>{winner ? `${profiles[winner].name} ${t("game.wins")}` : t("game.draw")}</strong>
          <div>{stateRef.current.players.map(({ slot, profile }) => `${profile.name} ${stateRef.current.scores[slot]}`).join(" : ")}</div>
          {phase === "roundOver" ? <button type="button" onClick={continueRound}>{t("game.continue")}</button> : (
            <div className="game-result-actions">
              <button type="button" onClick={restartMatch}>{t("game.restart")}</button>
              {onSelectMap && <button type="button" onClick={onSelectMap}>{t("game.selectMap")}</button>}
              <button type="button" onClick={onLeave}>{t("game.leave")}</button>
            </div>
          )}
        </div>
      )}
      <button type="button" className="game-leave" onClick={onLeave}>{t("game.leave")}</button>
    </ScaledStage>
  );
};
